import { Trans, useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useState } from "react";
import { TextField, Label } from "react-aria-components";
import { Loader2, TriangleAlert } from "lucide-react";
import type { FacilityStats } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { Dialog, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useShowError } from "../../error/ErrorProvider.js";
import { useFacilityStats, useDeleteFacility, type FacilityMutationError } from "./useFacilityMutations.js";

export interface DeleteFacilityDialogProps {
  /** The facility to delete. `name` is the client tree's (possibly stale) name — the dialog confirms against the
   *  server's authoritative `stats.name`. Mounted only while a delete is in progress (fresh state per open). */
  facility: { id: number; name: string };
  session: AuthSession | null;
  onClose: () => void;
  /** Announce a successful delete to the parent's aria-live region. */
  onDeleted: (name: string) => void;
}

/**
 * Hard-delete confirmation (ADR-006 §3/§4, RED-ZONE endpoint). Two-step, irreversible-cascade UX: fetch the "what's
 * connected" **stats** (fresh each open — `gcTime: 0`), show it, and require **typing the facility's exact name**
 * (matched against the server's `stats.name`, not the tree's) before the destructive action enables. Confirm sends the
 * `expected` snapshot token; the server 409s (`FACILITY_STATS_STALE`) if the subtree changed since — handled inline
 * with a Refresh. Other errors route to the shared error dialog. Loading / 403 / 404 are handled explicitly.
 */
export function DeleteFacilityDialog({ facility, session, onClose, onDeleted }: DeleteFacilityDialogProps) {
  const { t } = useTranslation();
  const stats = useFacilityStats(session, facility.id);
  const del = useDeleteFacility(session);
  const showError = useShowError();
  const [typed, setTyped] = useState("");

  // Authoritative (server) name once stats load; trimmed so an invisible trailing space in the data doesn't make the
  // name un-typeable. The typed value is compared EXACTLY (not trimmed) — a whitespace-padded string must NOT satisfy
  // this destructive confirmation (panel). Also require fresh stats: after a 409 Refresh, `refetch()` keeps the OLD
  // `data` (and its stale `expected` token) until it resolves, so gating on `!isFetching` stops a resubmit of the
  // stale token in that window (panel).
  const confirmName = (stats.data?.name ?? facility.name).trim();
  const canConfirm = stats.data != null && !stats.isFetching && typed === confirmName && !del.isPending;
  const isStale = del.isError && (del.error as FacilityMutationError | undefined)?.code === "FACILITY_STATS_STALE";

  const close = () => {
    del.reset();
    onClose();
  };
  const onConfirm = () => {
    if (!stats.data || del.isPending) return;
    del.mutate(
      { id: facility.id, expected: stats.data.expected },
      {
        onSuccess: () => {
          onDeleted(confirmName);
          close();
        },
        onError: (err) => {
          // 409 STALE is recoverable in-place (Refresh below); anything else (403/404/500) → shared dialog + close.
          if ((err as FacilityMutationError).code !== "FACILITY_STATS_STALE") {
            showError(err);
            close();
          }
        },
      },
    );
  };
  // Recover from a stale confirmation: fresh token + counts, clear the typed name, AND reset the mutation so its 409
  // error doesn't stay rendered after fresh stats arrive (panel #5).
  const onRefresh = () => {
    del.reset();
    setTyped("");
    void stats.refetch();
  };

  return (
    <Dialog isOpen isDismissable={!del.isPending} onOpenChange={(open) => !open && !del.isPending && close()}>
      <DialogTitle>{t("facilityManagement.delete.title")}</DialogTitle>

      {stats.isPending ? (
        <p className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 aria-hidden="true" className="size-4 animate-spin" /> {t("facilityManagement.delete.checking")}
        </p>
      ) : stats.isError ? (
        <>
          <DialogDescription>{statsErrorCopy(stats.error, t)}</DialogDescription>
          <DialogFooter>
            <Button onPress={close}>
              {t("facilityManagement.actions.close")}
            </Button>
          </DialogFooter>
        </>
      ) : stats.data ? (
        <>
          <DialogDescription>
            {t("facilityManagement.delete.permanently")} <strong className="text-foreground">{confirmName}</strong>
            {hasContent(stats.data) ? t("facilityManagement.delete.andEverything") : t("facilityManagement.delete.justDot")}
          </DialogDescription>
          {hasContent(stats.data) ? (
            <ul className="text-muted-foreground -mt-1 flex list-disc flex-col gap-0.5 pl-5 text-sm">
              {contentLines(stats.data, t).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : null}
          <p className="text-destructive text-sm font-medium">{t("facilityManagement.delete.cannotUndo")}</p>
          {stats.data.linkedChildCount > 0 ? (
            <p className="text-destructive text-sm">
              {t("facilityManagement.delete.alsoDeletes")}{" "}
              {t("facilityManagement.delete.linkedCopies", { count: stats.data.linkedChildCount })}
            </p>
          ) : null}
          {stats.data.trashedManuals > 0 ? (
            <p className="text-destructive text-sm">
              {t("facilityManagement.delete.alsoDeletes")}{" "}
              {t("facilityManagement.delete.trashedManuals", { count: stats.data.trashedManuals })}
            </p>
          ) : null}

          <TextField
            value={typed}
            onChange={setTyped}
            className="flex flex-col gap-1.5"
            isDisabled={del.isPending || stats.isFetching}
          >
            <Label className="text-sm">
              <Trans
                i18nKey="facilityManagement.delete.typeToConfirm"
                components={{ name: <strong className="text-foreground">{confirmName}</strong> }}
              />
            </Label>
            {/* Focus the confirm field on open — intentional for a type-to-confirm modal (react-aria traps focus). */}
            <Input autoFocus placeholder={confirmName} className="bg-background" />
          </TextField>

          {isStale ? (
            <div role="alert" className="text-destructive flex flex-col items-start gap-2 text-sm">
              <span className="flex items-center gap-1.5">
                <TriangleAlert aria-hidden="true" className="size-4 shrink-0" />
                {t("facilityManagement.delete.stale")}
              </span>
              <Button variant="outline" size="sm" onPress={onRefresh}>
                {t("facilityManagement.actions.refresh")}
              </Button>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="ghost" onPress={close} isDisabled={del.isPending}>
              {t("other.cancelButton")}
            </Button>
            <Button variant="destructive" onPress={onConfirm} isDisabled={!canConfirm}>
              {del.isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                  {t("facilityManagement.delete.deleting")}
                </>
              ) : (
                t("facilityManagement.delete.confirm")
              )}
            </Button>
          </DialogFooter>
        </>
      ) : null}
    </Dialog>
  );
}

/** Does the subtree contain anything beyond the node itself? Drives "and everything in it:" + the bulleted list. */
function hasContent(s: FacilityStats): boolean {
  return s.descendants > 0 || s.categories > 0 || s.manuals > 0;
}

/** The non-zero "what's connected" facts, one per bullet. `manuals` is 0 until the Manuals module, so its line simply
 *  doesn't render yet (forward-compatible). The archived count rides on the subfacilities line. */
function contentLines(s: FacilityStats, t: TFunction): string[] {
  const K = "facilityManagement.delete";
  const lines: string[] = [];
  if (s.descendants > 0) {
    // Three hand-written English plurals lived here — `n === 1 ? "subfacility" : "subfacilities"` and two more.
    // They were correct only for English AND hardcoded the domain nouns, so no tenant could rename them. i18next
    // plural forms whose nouns come from the glossary do both jobs.
    const archived = s.archivedDescendants > 0 ? t(`${K}.archivedSuffix`, { count: s.archivedDescendants }) : "";
    lines.push(`${t(`${K}.contentSubfacilities`, { count: s.descendants })}${archived}`);
  }
  if (s.categories > 0) lines.push(t(`${K}.contentCategoryLinks`, { count: s.categories }));
  if (s.manuals > 0) lines.push(t(`${K}.contentManuals`, { count: s.manuals }));
  return lines;
}

/** Friendly copy for a stats fetch failure (the first call — also catches a client-gate false-green: a hidden
 *  ungranted descendant makes the server 403 though the kebab enabled Delete). */
function statsErrorCopy(err: unknown, t: TFunction): string {
  const code = (err as FacilityMutationError | undefined)?.code;
  if (code === "FACILITY_FORBIDDEN") return t("facilityManagement.delete.forbidden");
  if (code === "FACILITY_NOT_FOUND") return t("facilityManagement.delete.gone");
  return t("facilityManagement.delete.loadFailed");
}
